import React, { useState, useCallback, useMemo } from 'react';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TextInput,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import { useCommentAliasesQuery } from '../../../../common/queries/use-comment-aliases-query';
import { AuthorAlias } from '../../../../services/comment-aliases';
import './identity-switcher.scss';

export type SelectedIdentity = {
  type: 'alias';
  id: number;
  displayName: string;
  avatarUrl: string | null;
} | null;

type IdentitySwitcherProps = {
  advocate: { avatar: { url: string | null } };
  selectedIdentity: SelectedIdentity;
  onIdentityChange: (identity: SelectedIdentity) => void;
  disabled?: boolean;
};

export function IdentitySwitcher({
  advocate,
  selectedIdentity,
  onIdentityChange,
  disabled,
}: IdentitySwitcherProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { aliases } = useCommentAliasesQuery();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (disabled) return;
      setIsOpen(open);
      if (!open) setSearchText('');
    },
    [disabled]
  );

  const handleSelectAlias = useCallback(
    (alias: AuthorAlias) => {
      onIdentityChange({
        type: 'alias',
        id: alias.id,
        displayName: alias.display_name,
        avatarUrl: alias.avatar_url,
      });
      setSearchText('');
    },
    [onIdentityChange]
  );

  const handleSelectMyProfile = useCallback(() => {
    onIdentityChange(null);
    setSearchText('');
  }, [onIdentityChange]);

  const filteredAliases = useMemo(() => {
    const seen = new Set<number>();
    return aliases.filter((a) => {
      if (seen.has(a.id)) return false;
      seen.add(a.id);
      if (selectedIdentity?.id === a.id) return false;
      if (!searchText) return true;
      return a.display_name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [aliases, searchText, selectedIdentity]);

  const myProfileLabel = t(
    'comments.identity_switcher.my_profile',
    'My profile'
  );
  const showMyProfile =
    selectedIdentity !== null &&
    (!searchText ||
      myProfileLabel.toLowerCase().includes(searchText.toLowerCase()));

  const showNoResults = filteredAliases.length === 0 && !showMyProfile;

  const currentAvatarUrl = selectedIdentity?.avatarUrl ?? advocate.avatar.url;
  const tooltipLabel = selectedIdentity?.displayName ?? myProfileLabel;

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <TooltipRoot delayDuration={0} open={isOpen ? false : undefined}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              className={`identity-switcher__trigger${
                disabled ? ' identity-switcher__trigger--disabled' : ''
              }`}
              type="button"
              aria-label={t(
                'comments.identity_switcher.label',
                'Switch identity'
              )}
              aria-disabled={disabled || undefined}
            >
              <Avatar
                size="medium"
                shape="circle"
                imgSrc={currentAvatarUrl || undefined}
              />
              <span className="identity-switcher__chevron">
                <Icon size={18} className="text-black-60">
                  expand_more
                </Icon>
              </span>
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent description={tooltipLabel} sideOffset={5} />
      </TooltipRoot>

      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="identity-switcher__dropdown"
      >
        <div className="identity-switcher__search">
          <TextInput
            type="search"
            size="compact"
            fullWidth
            placeholder={t('comments.identity_switcher.search', 'Search')}
            value={searchText}
            onChange={setSearchText}
            onKeyDown={(e) => e.stopPropagation()}
            rightWidget={<Icon size={18}>search</Icon>}
          />
        </div>

        {showMyProfile && (
          <DropdownMenuItem
            onSelect={handleSelectMyProfile}
            aria-selected={selectedIdentity === null}
          >
            <Avatar
              size="small"
              shape="circle"
              imgSrc={advocate.avatar.url || undefined}
            />
            <span className="identity-switcher__option-name">
              {myProfileLabel}
            </span>
          </DropdownMenuItem>
        )}

        {filteredAliases.map((alias) => (
          <DropdownMenuItem
            key={alias.id}
            onSelect={() => handleSelectAlias(alias)}
            aria-selected={selectedIdentity?.id === alias.id}
          >
            <Avatar
              size="small"
              shape="circle"
              imgSrc={alias.avatar_url || undefined}
            />
            <span className="identity-switcher__option-name">
              {alias.display_name}
            </span>
          </DropdownMenuItem>
        ))}

        {showNoResults && (
          <div className="identity-switcher__empty text-black-60">
            {t('comments.identity_switcher.no_results', 'No results')}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
